import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ThemeSelect.css";

const ThemeSelect = ({
  value,
  options = [],
  onChange,
  className = "",
  menuClassName = "",
  variant = "default", // default | status
  statusTone = "amber", // amber | green
  ariaLabel = "Select",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, minWidth: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const selected =
    options.find((opt) => String(opt.value) === String(value)) || options[0];
  const displayLabel = selected?.label ?? value ?? "";

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const preferredWidth = Math.max(rect.width, 140);
    let left = rect.left;
    if (left + preferredWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - preferredWidth - 12);
    }

    setMenuStyle({
      top: rect.bottom + 6,
      left,
      minWidth: preferredWidth,
    });
  };

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const handleScroll = () => setOpen(false);
    const handleResize = () => updateMenuPosition();
    const handleClickOutside = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={[
          "sx-theme-select",
          `sx-theme-select--${variant}`,
          variant === "status" ? `sx-theme-select--${statusTone}` : "",
          open ? "is-open" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <span className="sx-theme-select__label">{displayLabel}</span>
        <svg
          className="sx-theme-select__chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            className={["sx-theme-select-menu", menuClassName]
              .filter(Boolean)
              .join(" ")}
            style={{
              top: menuStyle.top,
              left: menuStyle.left,
              minWidth: menuStyle.minWidth,
            }}
          >
            {options.map((option) => {
              const isActive = String(option.value) === String(value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={[
                    "sx-theme-select-option",
                    isActive ? "is-active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
};

export default ThemeSelect;
